import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader2, Activity } from 'lucide-react';
import { toast } from 'sonner';

const AuthCallback = () => {
    const navigate = useNavigate();
    const { loginWithGoogle, setUserDirectly } = useAuth();
    const hasProcessed = useRef(false);

    useEffect(() => {
        // Prevent double processing in StrictMode
        if (hasProcessed.current) return;
        hasProcessed.current = true;

        const processAuth = async () => {
            try {
                // Extract session_id from URL fragment
                const hash = window.location.hash;
                const params = new URLSearchParams(hash.substring(1));
                const sessionId = params.get('session_id');

                if (!sessionId) {
                    toast.error('Authentication failed - no session ID');
                    navigate('/login');
                    return;
                }

                // Get stored OAuth data
                const accountType = sessionStorage.getItem('oauth_account_type') || 'individual';
                const companyName = sessionStorage.getItem('oauth_company_name');
                const country = sessionStorage.getItem('oauth_country');

                // Clear stored data
                sessionStorage.removeItem('oauth_account_type');
                sessionStorage.removeItem('oauth_company_name');
                sessionStorage.removeItem('oauth_country');

                // Exchange session_id for user data
                const userData = await loginWithGoogle(sessionId, accountType, companyName, country);
                
                toast.success(`Welcome${userData.name ? `, ${userData.name}` : ''}!`);
                
                // Navigate to dashboard with user data to skip auth check
                navigate('/dashboard', { state: { user: userData }, replace: true });
                
            } catch (error) {
                console.error('Auth callback error:', error);
                toast.error('Authentication failed. Please try again.');
                navigate('/login');
            }
        };

        processAuth();
    }, [loginWithGoogle, navigate, setUserDirectly]);

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-sky-50/30">
            <div className="text-center">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#1E3A5F] to-[#0D2137] flex items-center justify-center mx-auto mb-6 shadow-xl">
                    <Activity className="w-8 h-8 text-teal-400 animate-pulse" />
                </div>
                <Loader2 className="w-8 h-8 animate-spin text-teal-500 mx-auto mb-4" />
                <p className="text-slate-600 font-medium">Completing sign in...</p>
                <p className="text-sm text-slate-500 mt-1">Please wait while we set up your account</p>
            </div>
        </div>
    );
};

export default AuthCallback;
